import { useCallback, useRef } from "react";
import classNames from "classnames";
import useEventListener from "@use-it/event-listener";
import OutsideClick from "../../../functions/OutsideClick";
import { CURRENT, SHORT_MONTH_NAMES, VIEW_NAMES, VIEW_OF_MONTHS } from "../../../functions/calendarNav";
import { getYearList } from "../../../functions/serviceFunctions";

// Keys of the bar with a menu under them. A press outside them, or Esc, closes it; their own keys don't.
function Slot({ open, onClose, menu, className, children }) {
    const ref = useRef(null);
    const close = useCallback(() => { if (open) onClose(); }, [open, onClose]);
    OutsideClick(ref, close);
    useEventListener('keydown', (e) => { if (e.key === 'Escape') close(); }, window);

    return (
        <div className={classNames('view-bar__slot', className)} ref={ref}>
            {children}
            {open && <div className="view-bar__menu">{menu}</div>}
        </div>
    );
}

// Ctrl+click (⌘+click on a Mac) opens a key's settings instead of doing what it does. A Mac turns Ctrl+click
// into a context menu, so that opens them too.
const withSettings = (openSettings, action) => ({
    onClick: (e) => (e.ctrlKey || e.metaKey ? openSettings() : action()),
    onContextMenu: (e) => {
        if (!e.ctrlKey) return;
        e.preventDefault();
        openSettings();
    },
});

// A grid of choices with the chosen one marked.
function Options({ options, value, onChoose, columns, className }) {
    return (
        <div className={classNames('view-bar__options', className)} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {options.map(option => (
                <button type="button" key={option.value}
                    className={classNames('view-bar__option', {
                        'view-bar__option--selected': option.value === value,
                        'view-bar__option--wide': option.wide,
                    })}
                    onClick={() => onChoose(option.value)}>{option.label}</button>
            ))}
        </div>
    );
}

const STEPS = [{ label: 'Неделя', value: 'week' }, { label: 'Месяц', value: 'month' }];

function StepMenu({ step, onChoose }) {
    return <>
        <p className="view-bar__menu-title">Шаг стрелок</p>
        <Options options={STEPS} value={step} onChoose={onChoose} columns={2} className="view-bar__step-options" />
        <p className="view-bar__menu-hint">В виде дня — день, в виде месяца — месяц</p>
    </>;
}

const current = (label) => ({ label, value: CURRENT, wide: true });
const MONTHS = [current('Текущий'), ...SHORT_MONTH_NAMES.map((label, value) => ({ label, value }))];
const VIEWS = Object.keys(VIEW_NAMES).map(value => ({ label: value, value: +value }));

// A home key's month, year and view; each choice is kept at once and the menu stays open for the others. Until a
// view is chosen the home keeps the one the calendar is in, which the menu shows chosen.
function HomeMenu({ number, home, layout, onChange, onAdd, onRemove, onClose }) {
    const view = home.view === CURRENT
        ? Number(Object.keys(VIEW_OF_MONTHS).find(months => VIEW_OF_MONTHS[months] === layout))
        : home.view;
    const years = [current('Текущий'), ...getYearList().map(year => ({ label: year, value: year }))];
    return <>
        <div className="view-bar__menu-head">
            <p className="view-bar__menu-title">Домик {number}</p>
            <button type="button" className="view-bar__menu-close" title="Закрыть" aria-label="Закрыть" onClick={onClose}>✕</button>
        </div>
        <p className="view-bar__menu-caption">Месяц</p>
        <Options options={MONTHS} value={home.month} onChoose={(month) => onChange({ month })} columns={4} className="view-bar__home-months" />
        <p className="view-bar__menu-caption">Год</p>
        <Options options={years} value={home.year} onChoose={(year) => onChange({ year })} columns={4} className="view-bar__home-years" />
        <p className="view-bar__menu-caption">Вид, месяцев</p>
        <Options options={VIEWS} value={view} onChoose={(view) => onChange({ view })} columns={4} className="view-bar__home-views" />
        {(onAdd || onRemove) &&
            <div className="view-bar__menu-actions">
                {onAdd && <button type="button" className="view-bar__menu-action view-bar__home-add" onClick={onAdd}>+ Добавить домик</button>}
                {onRemove && <button type="button" className="view-bar__menu-action view-bar__home-remove" onClick={onRemove}>Удалить</button>}
            </div>}
    </>;
}

function YearMenu({ year, onChoose }) {
    return <Options options={getYearList().map(value => ({ label: value, value }))} value={year} onChoose={onChoose} columns={3}
        className="view-bar__years" />;
}

export { Slot, withSettings, StepMenu, HomeMenu, YearMenu };
